import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  useTheme,
  alpha,
  IconButton,
  CircularProgress
} from '@mui/material';
import {
  EmojiEvents as EmojiEventsIcon,
  People as PeopleIcon,
  Star as StarIcon,
  CheckCircle as CheckCircleIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useSEO } from '../hooks/useSEO';

// 导入图片
import case1 from '../assets/images/cases/image_24.jpg';
import case2 from '../assets/images/cases/image_25.jpg';
import case3 from '../assets/images/cases/image_26.jpg';
import case4 from '../assets/images/cases/image_27.jpg';
import case5 from '../assets/images/cases/image_28.jpg';

// 带加载效果的图片组件
const ImageWithLoading = ({ src, alt, style, sx, ...props }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const handleLoad = () => setLoading(false);
  const handleError = () => { setLoading(false); setError(true); };

  return (
    <Box sx={{ position: 'relative', width: '100%', minHeight: loading ? '200px' : 'auto', ...sx }}>
      {loading && (
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(0,0,0,0.05)', zIndex: 1 }}>
          <CircularProgress size={48} />
        </Box>
      )}
      {error ? (
        <Box sx={{ width: '100%', minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(0,0,0,0.05)', color: 'text.secondary' }}>
          <Typography variant="body2">图片加载失败</Typography>
        </Box>
      ) : (
        <img
          src={src}
          alt={alt}
          style={{ ...style, opacity: loading ? 0 : 1, transition: 'opacity 0.3s ease', width: '100%', visibility: loading ? 'hidden' : 'visible' }}
          onLoad={handleLoad}
          onError={handleError}
          {...props}
        />
      )}
    </Box>
  );
};

// 图片画廊组件（网格视图 + 轮播视图）
const ImageGallery = ({ items, title }) => {
  const theme = useTheme();
  const [isCarouselMode, setIsCarouselMode] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const getPrevIndex = (index) => (index - 1 + items.length) % items.length;
  const getNextIndex = (index) => (index + 1) % items.length;
  const prevIndex = getPrevIndex(currentIndex);
  const nextIndex = getNextIndex(currentIndex);

  const handleImageClick = (index) => { setCurrentIndex(index); setIsCarouselMode(true); };
  const handleCloseCarousel = () => setIsCarouselMode(false);

  if (!isCarouselMode) {
    return (
      <Grid container spacing={3}>
        {items.map((item, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Box
              onClick={() => handleImageClick(index)}
              sx={{ cursor: 'pointer', position: 'relative', borderRadius: '8px', overflow: 'hidden', boxShadow: theme.shadows[2], transition: 'all 0.3s ease', '&:hover': { transform: 'scale(1.02)', boxShadow: theme.shadows[6] } }}
            >
              <ImageWithLoading src={item.image} alt={item.name} style={{ width: '100%', height: 'auto', display: 'block' }} />
              <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, bgcolor: 'rgba(0,0,0,0.7)', color: 'white', p: 2, textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>{item.name}</Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>点击查看详情</Typography>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <IconButton onClick={handleCloseCarousel} sx={{ bgcolor: 'rgba(0,0,0,0.5)', color: 'white', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
        {/* 左侧缩略图 */}
        <Box onClick={() => setCurrentIndex(prevIndex)} sx={{ cursor: 'pointer', opacity: 0.6, transition: 'all 0.3s ease', position: 'relative', flex: '1 1 auto', maxWidth: { xs: 'none', md: '20%' }, display: { xs: 'none', md: 'block' }, '&:hover': { opacity: 1, transform: 'scale(1.05)' } }}>
          <ImageWithLoading src={items[prevIndex].image} alt={items[prevIndex].name} style={{ width: '100%', height: 'auto', borderRadius: '8px', boxShadow: theme.shadows[2] }} />
          <Box sx={{ position: 'absolute', bottom: 8, left: 8, right: 8, bgcolor: 'rgba(0,0,0,0.6)', color: 'white', p: 1, borderRadius: '4px', textAlign: 'center' }}>
            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>{items[prevIndex].name}</Typography>
          </Box>
        </Box>

        {/* 中间主图 - 占60%宽度 */}
        <Box sx={{ position: 'relative', width: { xs: '100%', md: '60%' }, flexShrink: 0 }}>
          <IconButton onClick={() => setCurrentIndex(prevIndex)} sx={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', zIndex: 3, bgcolor: 'rgba(255,255,255,0.9)', boxShadow: theme.shadows[4], '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}>
            <ChevronLeftIcon />
          </IconButton>
          <Box sx={{ width: '100%', position: 'relative', maxHeight: '600px', overflow: 'hidden', borderRadius: '8px' }}>
            <ImageWithLoading src={items[currentIndex].image} alt={items[currentIndex].name} style={{ width: '100%', height: 'auto', maxHeight: '600px', objectFit: 'contain', display: 'block' }} />
            <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, bgcolor: 'rgba(0,0,0,0.7)', color: 'white', p: 2, textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>{items[currentIndex].name}</Typography>
              <Typography variant="body2">【点击查看】{items[currentIndex].name}详情</Typography>
            </Box>
          </Box>
          <IconButton onClick={() => setCurrentIndex(nextIndex)} sx={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', zIndex: 3, bgcolor: 'rgba(255,255,255,0.9)', boxShadow: theme.shadows[4], '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}>
            <ChevronRightIcon />
          </IconButton>
        </Box>

        {/* 右侧缩略图 */}
        <Box onClick={() => setCurrentIndex(nextIndex)} sx={{ cursor: 'pointer', opacity: 0.6, transition: 'all 0.3s ease', position: 'relative', flex: '1 1 auto', maxWidth: { xs: 'none', md: '20%' }, display: { xs: 'none', md: 'block' }, '&:hover': { opacity: 1, transform: 'scale(1.05)' } }}>
          <ImageWithLoading src={items[nextIndex].image} alt={items[nextIndex].name} style={{ width: '100%', height: 'auto', borderRadius: '8px', boxShadow: theme.shadows[2] }} />
          <Box sx={{ position: 'absolute', bottom: 8, left: 8, right: 8, bgcolor: 'rgba(0,0,0,0.6)', color: 'white', p: 1, borderRadius: '4px', textAlign: 'center' }}>
            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>{items[nextIndex].name}</Typography>
          </Box>
        </Box>
      </Box>

      <Typography variant="body2" sx={{ textAlign: 'center', mt: 2, color: 'text.secondary' }}>{currentIndex + 1} / {items.length}</Typography>
    </Box>
  );
};

const SuccessCases = () => {
  const theme = useTheme();
  
  // 设置页面 SEO
  useSEO('success-cases');

  // 成功案例数据
  const successCases = [
    { name: '成功案例1', image: case1 },
    { name: '成功案例2', image: case2 },
    { name: '成功案例3', image: case3 },
    { name: '成功案例4', image: case4 },
    { name: '成功案例5', image: case5 }
  ];

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* 英雄区域 */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
          py: 8,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography 
              variant="h2" 
              sx={{ 
                mb: 3, 
                fontWeight: 700,
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              成功案例
            </Typography>
            <Typography variant="h5" sx={{ color: 'text.secondary', lineHeight: 1.8, mb: 2 }}>
              10年专业经验，5000+成功案例，98%录取成功率
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: 'text.secondary' }}>
              希航成立至今十年，已服务<strong style={{ color: '#bf0202' }}>5000+</strong>学生申请，申请学校涵盖大马热门公私立学校30余个，本硕博录取成功率保持在<strong style={{ color: '#bf0202' }}>98%</strong>以上。
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* 成功案例展示 */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#333', textAlign: 'center' }}>
            成功案例展示（部分）：
          </Typography>
          <ImageGallery items={successCases} title="成功案例" />
        </Box>

        {/* 成功案例统计 */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" sx={{ textAlign: 'center', mb: 6, fontWeight: 600 }}>
            成功案例统计
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ p: 3, textAlign: 'center', height: '100%' }}>
                <CardContent>
                  <Box sx={{ width: 80, height: 80, mx: 'auto', mb: 3, bgcolor: 'primary.main', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <PeopleIcon sx={{ fontSize: 40, color: 'white' }} />
                  </Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
                    5000+
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    成功案例
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    已成功帮助超过5000名学生实现留学梦想
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ p: 3, textAlign: 'center', height: '100%' }}>
                <CardContent>
                  <Box sx={{ width: 80, height: 80, mx: 'auto', mb: 3, bgcolor: 'secondary.main', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CheckCircleIcon sx={{ fontSize: 40, color: 'white' }} />
                  </Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: 'secondary.main', mb: 1 }}>
                    98%
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    录取成功率
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    本硕博录取成功率保持在98%以上
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ p: 3, textAlign: 'center', height: '100%' }}>
                <CardContent>
                  <Box sx={{ width: 80, height: 80, mx: 'auto', mb: 3, bgcolor: 'success.main', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <StarIcon sx={{ fontSize: 40, color: 'white' }} />
                  </Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: 'success.main', mb: 1 }}>
                    10+
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    年经验
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    深耕马来西亚留学领域，拥有丰富的申请经验
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ p: 3, textAlign: 'center', height: '100%' }}>
                <CardContent>
                  <Box sx={{ width: 80, height: 80, mx: 'auto', mb: 3, bgcolor: 'warning.main', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <EmojiEventsIcon sx={{ fontSize: 40, color: 'white' }} />
                  </Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: 'warning.main', mb: 1 }}>
                    30+
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    合作院校
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    与马来西亚30余所知名院校建立合作关系
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>


        {/* 联系我们 */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
            想了解我们的成功案例？
          </Typography>
          <Typography variant="h6" sx={{ color: 'text.secondary', mb: 4 }}>
            我们的专业顾问将为您详细介绍成功案例和申请经验
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default SuccessCases;
